/*
 * hp-okendo-feed.js — renders live Okendo reviews into hp-reviews-feed sections.
 * Fetches the Okendo Storefront REST API (public, read-only) and builds H&P
 * markup client-side — no Okendo CSS/JS/fonts. Lazy: fetch only when in view.
 *
 * Config comes from data-* attributes on [data-okendo-feed] (see hp-reviews-feed.liquid).
 * Review schema + endpoints: docs/okendo-migration-assessment.md §3.
 */
(function () {
  'use strict';
  if (window.__okrevOkendoFeed) return;       // guard: script may be injected per-section
  window.__okrevOkendoFeed = true;

  var API_BASE = 'https://api.okendo.io/v1/stores/';
  var API_ROOT = 'https://api.okendo.io/v1';   // nextUrl cursors are relative to this
  var MEDIA_BASE = 'https://media-dynamic.okendo.io/images/';
  // NB: do NOT send the 'okendo-api-version' header from the browser — it is not in
  // the API's CORS access-control-allow-headers, so a custom header triggers a
  // preflight that fails. The /v1/ path pins the version; a plain GET works.

  var PROD_AGG = {};   // productId -> aggregate (cache for lazy product ratings)
  var LB = null;       // shared lightbox element
  var STORE_ID = null; // subscriber id (for lightbox product-rating fills)
  var SWATCH_MAP = null; // { id: swatchUrl } for photoless-card colourway fills (per render)
  var CW_SWATCH_MAP = null; // { colourwayName: swatchUrl } global fallback (retired / variantId-null); lazy-loaded once
  var CW_URL = null;        // endpoint for the global fallback map (set per render from data-okrev-cw-url)
  var CW_PROMISE = null;    // in-flight/settled fetch of the global map (started at most once)
  var CW_PENDING = [];      // feed els awaiting the global map to upgrade their pastel cards

  function esc(s) { var d = document.createElement('div'); d.textContent = s == null ? '' : s; return d.innerHTML; }

  // Merchant replies come back from Okendo as HTML (<p>, <a>, &#39;); everything else in the
  // payload is plain text. Flatten to text before esc() — DOMParser is inert, so nothing in
  // the markup runs or loads.
  function htmlText(s) {
    if (!s) return '';
    var src = String(s).replace(/<\/p>|<br\s*\/?>/gi, '\n');
    var txt = new DOMParser().parseFromString(src, 'text/html').body.textContent || '';
    return txt.replace(/[ \t]+\n/g, '\n').replace(/\n{2,}/g, '\n').trim();
  }

  // Crisp square media URL from the dynamicKey at the size we display (the API's
  // thumbnailUrl is only 120px and blurs when shown larger).
  function mediaUrl(m, px) {
    if (m && m.dynamicKey) return MEDIA_BASE + m.dynamicKey + '.jpg?d=' + px + 'x' + px + '&crop=center';
    return (m && (m.largeUrl || m.thumbnailUrl || m.fullSizeUrl)) || '';
  }

  // Signature for spotting the SAME review re-posted — Okendo serves same-author /
  // same-product duplicates as separate reviews and never merges them. Photo reviews
  // key on the first image's dynamicKey (unique per upload); photoless ones key on a
  // normalised body, but only when it's long enough that a collision means a real
  // duplicate, not a stock phrase like "Love it!". '' = don't de-dup this one.
  function reviewSig(r) {
    if (r.media && r.media.length && r.media[0].dynamicKey) return 'm:' + r.media[0].dynamicKey;
    var body = (r.body || '').toLowerCase().replace(/\s+/g, ' ').trim();
    return body.length >= 60 ? 't:' + body : '';
  }

  // Okendo redacts some names to the literal "[Filtered]" (profanity / PII filter).
  // Strip that token so we never render it (or use "[" as the avatar initial); a
  // fully-filtered name falls back to Anonymous, same as a blank one.
  function reviewerName(r) {
    var n = ((r.reviewer && r.reviewer.displayName) || '').replace(/\[filtered\]/gi, '').trim();
    return n || 'Anonymous';
  }

  function fmtRelative(iso) {
    if (!iso) return '';
    var then = new Date(iso);
    if (isNaN(then)) return '';
    var s = (Date.now() - then.getTime()) / 1000;
    if (s < 60) return 'just now';
    var units = [['year', 31536000], ['month', 2592000], ['week', 604800], ['day', 86400], ['hour', 3600], ['minute', 60]];
    for (var i = 0; i < units.length; i++) {
      var n = Math.floor(s / units[i][1]);
      if (n >= 1) return n + ' ' + units[i][0] + (n > 1 ? 's' : '') + ' ago';
    }
    return 'just now';
  }

  function getJSON(url) { return fetch(url).then(function (r) { if (!r.ok) throw new Error('Okendo ' + r.status); return r.json(); }); }

  // Okendo syncs a ROUNDED `average_rating` metafield ("5.0 out of 5.0"), and our JSON-LD reads it
  // server-side — so it can disagree with the PRECISE value the live API renders on-page (e.g. 4.97).
  // Google requires the structured rating to match the visible rating, so patch the Product JSON-LD's
  // aggregateRating from the same aggregate the feed displays. Product-scoped feeds only (PDPs).
  function patchProductJsonLd(aggregate) {
    if (!aggregate) return;
    var a = aggregate.reviewAggregate || aggregate;
    var count = a.reviewCount || a.ratingAndReviewCount || 0;
    var rc = a.ratingAndReviewCount || count;
    if (!rc) return;
    var avg = Math.round(((a.ratingAndReviewValuesTotal || 0) / rc) * 100) / 100;  // 2dp, matches the feed
    if (!avg) return;
    try {
      var scripts = document.querySelectorAll('script[type="application/ld+json"]');
      for (var i = 0; i < scripts.length; i++) {
        var j; try { j = JSON.parse(scripts[i].textContent); } catch (e) { continue; }
        var t = j['@type'];
        if ((t === 'Product' || (Array.isArray(t) && t.indexOf('Product') !== -1)) && j.aggregateRating) {
          j.aggregateRating.ratingValue = avg;
          if (count) j.aggregateRating.ratingCount = count;
          scripts[i].textContent = JSON.stringify(j);
          return;
        }
      }
    } catch (e) {}
  }

  // ── endpoints ──────────────────────────────────────────────────────────
  function basePath(el) {
    var store = el.getAttribute('data-store-id');
    if (!store) return null;
    var base = API_BASE + encodeURIComponent(store);
    var source = el.getAttribute('data-source');
    var pid = el.getAttribute('data-product-id');
    var cid = el.getAttribute('data-collection-id');
    // Product/collection sources must NOT fall back to store-wide when they lack an id
    // (that surfaces unrelated reviews — e.g. Current product on a non-product page).
    if (source === 'current_product' || source === 'product') return pid ? base + '/products/' + encodeURIComponent(pid) : null;
    if (source === 'collection' || source === 'current_collection') return cid ? base + '/collections/' + encodeURIComponent(cid) : null;
    return base;
  }
  // Grouped PDP: the /products/{pid} endpoint returns the whole colourway group, and we float
  // the current product's reviews to the front. Centralised so reviewsUrl / render / load-more agree.
  function isGrouped(el) {
    return el.getAttribute('data-source') === 'current_product'
      && el.getAttribute('data-group') === 'true'
      && !!el.getAttribute('data-product-id')
      && el.getAttribute('data-layout') !== 'photo_wall';
  }
  function reviewsUrl(el) {
    var p = basePath(el); if (!p) return null;
    var limit = parseInt(el.getAttribute('data-limit'), 10) || 12;
    var orderBy = el.getAttribute('data-order-by') || 'date desc';
    if (el.getAttribute('data-require-media') === 'true') orderBy = 'has_media desc';
    // Keyword filtering happens client-side, so pull the max pool to filter from.
    // Grouped PDP: over-fetch a pool so the current product's reviews — scattered through the group
    // by date — float to the front across the first few pages, not just within page 1. Load more
    // reveals this pool (current-product-first) before paging the cursor, which continues gaplessly
    // right after the pool.
    // Plain load-more feeds fetch exactly `limit` so the cursor continues with no gap; others
    // over-fetch a small buffer so filters can't leave the first page short.
    var fetchN = el.getAttribute('data-keyword') ? 25
      : isGrouped(el) ? Math.min(60, limit * 4)
      : (el.getAttribute('data-load-more') === 'true' ? limit : Math.min(25, limit + 8));
    return p + '/reviews?limit=' + fetchN + '&orderBy=' + encodeURIComponent(orderBy);
  }
  function aggregateUrl(el) { var p = basePath(el); return p ? p + '/review_aggregate' : null; }
  function mediaStripUrl(el) { var p = basePath(el); return p ? p + '/reviews?limit=10&orderBy=' + encodeURIComponent('has_media desc') : null; }

  // ── filtering ──────────────────────────────────────────────────────────
  function filterReviews(reviews, el, noSlice) {
    var min = parseInt(el.getAttribute('data-min-rating'), 10) || 0;
    var requireMedia = el.getAttribute('data-require-media') === 'true';
    var limit = parseInt(el.getAttribute('data-limit'), 10) || 12;
    var source = el.getAttribute('data-source');
    var pid = el.getAttribute('data-product-id');
    // Grouped PDP: the /products/{pid} endpoint returns the whole review group
    // (every sibling colorway). When grouped, keep the siblings; otherwise strip
    // them so a product source shows only its own reviews.
    var grouped = isGrouped(el);
    var strictProduct = (source === 'current_product' || source === 'product') && pid && !grouped;
    // Client-side keyword filter — keep only reviews whose body/title mentions a term.
    var terms = (el.getAttribute('data-keyword') || '').toLowerCase().split(',').map(function (s) { return s.trim(); }).filter(Boolean);
    // De-dup signatures: reset on a fresh render (!noSlice), persist across load-more
    // appends (noSlice) so a re-posted review never shows twice across pages.
    if (!noSlice) el.__okrevSeen = {};
    var seen = el.__okrevSeen || (el.__okrevSeen = {});
    var out = reviews.filter(function (r) {
      if (strictProduct && r.productId !== pid) return false;
      if (min && (r.rating || 0) < min) return false;
      if (requireMedia && !(r.media && r.media.length)) return false;
      if (terms.length) {
        var hay = ((r.body || '') + ' ' + (r.title || '')).toLowerCase();
        var hit = false;
        for (var i = 0; i < terms.length; i++) { if (hay.indexOf(terms[i]) >= 0) { hit = true; break; } }
        if (!hit) return false;
      }
      return true;
    });
    // Float the current colorway's reviews to the front, preserving the API's
    // secondary order within each partition (Array.prototype.sort is stable).
    if (grouped) {
      out = out.slice().sort(function (a, b) {
        return (a.productId === pid ? 0 : 1) - (b.productId === pid ? 0 : 1);
      });
    }
    return noSlice ? out : out.slice(0, limit);   // appended pages keep everything that passed
  }

  // ── building blocks ──────────────────────────────────────────────────────
  // Five individual stars so they can pop in one-by-one on reveal. Each star carries
  // its own fill % (supports fractional averages, e.g. 4.9 = four full + one at 90%)
  // and its index for the staggered animation-delay. The wrapper keeps the a11y label.
  function starsHtml(rating, extraClass) {
    var r = Math.max(0, Math.min(5, Number(rating) || 0));
    var stars = '';
    for (var i = 0; i < 5; i++) {
      var pct = Math.max(0, Math.min(1, r - i)) * 100;
      stars += '<span class="okrev-star" style="--okrev-star-pct:' + pct + '%;--okrev-star-i:' + i + '" aria-hidden="true"></span>';
    }
    return '<span class="okrev-stars' + (extraClass ? ' ' + extraClass : '') + '" role="img" aria-label="' +
      esc((Math.round(r * 10) / 10) + ' out of 5 stars') + '">' + stars + '</span>';
  }

  function badgesHtml(r) {
    // Okendo's storefront API only returns approved buyer reviews → all are verified.
    var b = '<span class="okrev-badge okrev-badge--verified">✓ Verified buyer</span>';
    if (r.isRecommended) b += '<span class="okrev-badge okrev-badge--rec">✓ Recommends</span>';
    return b;
  }

  // Suppress low-value titles that just restate the rating (e.g. "5 Stars").
  function cleanTitle(r) {
    var t = (r.title || '').trim();
    if (!t || /^(\d+(\.\d+)?|one|two|three|four|five)\s*stars?$/i.test(t)) return '';
    return t;
  }
  // Author block: avatar (initials) + "{name} recommends" + verified · date.
  function avatarHtml(r, idx) {
    var name = reviewerName(r);
    return '<span class="okrev-avatar" aria-hidden="true" style="background:' + TINTS[(idx || 0) % TINTS.length] + '">' +
      esc((name.charAt(0) || '?').toUpperCase()) + '</span>';   // rotates the H&P soft tints by card position
  }
  function authorBlock(r, idx) {
    var rec = r.isRecommended ? ' <span class="okrev-author__rec">recommends</span>' : '';
    var loc = r._pinLocation ? '<p class="okrev-author__loc">' + esc(r._pinLocation) + '</p>' : '';
    return '<div class="okrev-author">' + avatarHtml(r, idx) +
      '<div class="okrev-author__lines">' +
        '<p class="okrev-author__name">' + esc(reviewerName(r)) + rec + '</p>' + loc +
        '<p class="okrev-author__sub"><span class="okrev-author__verified">✓ Verified buyer</span> · ' + esc(fmtRelative(r.dateCreated)) + '</p>' +
      '</div></div>';
  }

  // No-photo hero on a rotating brand tint: the review title as an oversized serif quote,
  // or the H&P logo when there's no title (matches the predictive-search placeholder).
  var TINTS = ['#ffe1dd', '#dceabb', '#fbe5ca', '#e2cfe2'];
  var HP_LOGO = 'https://cdn.shopify.com/s/files/1/0017/9142/6675/files/h_p-logo-vector-overlay.png';
  // Liquid-injected JSON map from a sibling <script> (survives el.innerHTML). Read per render.
  function readJsonScript(el, attr) {
    try {
      var p = el.parentElement;
      var s = p && p.querySelector('[' + attr + ']');
      return s ? JSON.parse(s.textContent || 'null') : null;
    } catch (e) { return null; }
  }
  // Resolve a colourway swatch for a review. '' = no swatch → pastel fallback.
  //  1. Precise id-match (SWATCH_MAP): variant-colourway products key on variantId,
  //     separate-product colourways on productId. Ids may be "shopify-{n}" or bare {n}.
  //  2. Name-match fallback (CW_SWATCH_MAP, keyed by lowercased colourway name) — catches
  //     RETIRED colourways (product deleted → not in the id-map) and variantId-null reviews.
  //     Colourways are globally shared (one "Breve" everywhere) so name → swatch is unambiguous.
  function swatchFor(r) {
    if (SWATCH_MAP) {
      var ks = [r.variantId, r.productId];
      for (var i = 0; i < ks.length; i++) {
        var k = ks[i]; if (!k) continue;
        var bare = String(k).replace(/^shopify-/, '');
        if (SWATCH_MAP[bare]) return SWATCH_MAP[bare];
        if (SWATCH_MAP[k]) return SWATCH_MAP[k];
      }
    }
    var byName = cwSwatchForName(r.productVariantName, r.productName);
    if (byName) return byName;
    return '';
  }
  // Name-match against the global fallback map (retired colourways / variantId-null). Split out
  // so it can run both inline (swatchFor) and later, against a lazy-loaded map, to upgrade a card
  // that first rendered as pastel. Colourways are globally shared, so name → swatch is unambiguous.
  function cwSwatchForName(variantName, productName) {
    if (!CW_SWATCH_MAP) return '';
    // exact variant title (variant products, e.g. Meh Dai "Breve")
    var vn = (variantName || '').toLowerCase().replace(/\s+/g, ' ').trim();
    if (vn && CW_SWATCH_MAP[vn]) return CW_SWATCH_MAP[vn];
    // longest colourway-name prefix of the product name ("Breve Lark Baby Carrier" → "breve")
    var pn = (productName || '').toLowerCase().replace(/\s+/g, ' ').trim();
    var best = '';
    for (var name in CW_SWATCH_MAP) {
      if (pn === name || pn.indexOf(name + ' ') === 0) { if (name.length > best.length) best = name; }
    }
    return best ? CW_SWATCH_MAP[best] : '';
  }
  // Sentence-case a title only if it's SHOUTING (all-caps, no lowercase letters); leaves
  // normally-typed titles untouched. For the Lora hero quote only (the Lato all-caps content
  // title line keeps its casing). Internal proper nouns in an all-caps title can't be recovered.
  function deShout(s) {
    return (s && s === s.toUpperCase() && s !== s.toLowerCase())
      ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase()
      : s;
  }
  function noPhotoHero(r, tint) {
    var title = cleanTitle(r);
    var quote = title
      ? '<span class="okrev-card__quotemark" aria-hidden="true">“</span><p class="okrev-card__heroquote">' + esc(deShout(title)) + '</p>'
      : '';
    var sw = swatchFor(r);
    if (sw) {
      // Swatch fill: the swatch IS the visual, so NO h&p logo when there's no title — just
      // the cream scrim (+ the quote if present). Not the --logo variant, so the mobile
      // "hide logo hero" rule doesn't apply (we want the swatch to show everywhere).
      // Scrim does the legibility lifting — no grayscale, so the colourway keeps its colour.
      return '<div class="okrev-card__quotehero okrev-card__quotehero--swatch" style="background-image:url(' + esc(sw) + ')">' +
        '<span class="okrev-card__scrim" aria-hidden="true"></span>' + quote + '</div>';
    }
    // Pastel fallback: title → quote, otherwise the h&p logo placeholder.
    var inner = quote || '<img class="okrev-card__herologo" src="' + HP_LOGO + '" alt="" width="84" height="84" loading="lazy">';
    // When a lazy retired-colourway map is configured, tag the hero with the review's names so
    // ensureCwMap → upgradePastelCards can swap in a swatch if the colourway turns out to be retired.
    var cwTag = CW_URL ? ' data-okrev-cwvn="' + esc(r.productVariantName || '') + '" data-okrev-cwpn="' + esc(r.productName || '') + '"' : '';
    return '<div class="okrev-card__quotehero' + (title ? '' : ' okrev-card__quotehero--logo') + '"' + cwTag +
      ' style="background:' + TINTS[tint % TINTS.length] + '">' + inner + '</div>';
  }

  // Full body, line-clamped via CSS; Read-more (revealed post-render only if clamped) → lightbox.
  function bodyBlock(r, idx) {
    if (!r.body) return '';
    return '<p class="okrev-card__text okrev-card__text--clamp" data-okrev-clamp>' + esc(r.body) + '</p>' +
      '<button type="button" class="okrev-card__more" data-okrev-more="' + idx + '" hidden>Read more</button>';
  }

  function tileHtml(m, px, cls, fallbackAlt) {
    var url = mediaUrl(m, px); if (!url) return '';
    var vid = m.type === 'video' ? ' ' + cls + '--video' : '';
    return '<div class="' + cls + vid + '"><img src="' + esc(url) + '" alt="' + esc(m.alt || fallbackAlt || '') + '" loading="lazy"></div>';
  }

  // Big image area with scroll-snap slides + prev/next arrows (multi-image).
  function heroHtml(media, px, phPx) {
    if (!media || !media.length) return '';
    var slides = media.map(function (m) {
      var hi = mediaUrl(m, px || 700);
      if (!hi) return '';
      var lo = mediaUrl(m, phPx || 240);   // low-res placeholder (matches a cached size → instant)
      var vid = m.type === 'video' ? ' okrev-hero__slide--video' : '';
      return '<div class="okrev-hero__slide' + vid + '" style="background-image:url(' + lo + ')">' +
        '<img src="' + esc(hi) + '" alt="' + esc(m.alt || '') + '" loading="lazy" decoding="async" data-okrev-fade></div>';
    }).join('');
    var nav = media.length > 1
      ? '<button type="button" class="okrev-hero__nav okrev-hero__nav--prev" data-okrev-prev aria-label="Previous photo">‹</button>' +
        '<button type="button" class="okrev-hero__nav okrev-hero__nav--next" data-okrev-next aria-label="Next photo">›</button>'
      : '';
    return '<div class="okrev-hero"><div class="okrev-hero__track">' + slides + '</div>' + nav + '</div>';
  }

  // List/lightbox no-photo media: a colourway swatch only — never the title-quote or logo
  // hero (the title already shows in the content pane, and a placeholder tile with nothing
  // to show is a wasted slot on a list row). If the local id-map has no match but a retired-
  // colourway lookup could still resolve one later (CW_URL configured), keep the slot hidden
  // and tagged so ensureCwMap → upgradePastelCards can reveal it in place; otherwise no media
  // pane at all — the row just runs full width.
  function listNoPhotoMedia(r) {
    var sw = swatchFor(r);
    if (sw) {
      return '<div class="okrev-block__media"><div class="okrev-card__quotehero okrev-card__quotehero--swatch" style="background-image:url(' + esc(sw) + ')">' +
        '<span class="okrev-card__scrim" aria-hidden="true"></span></div></div>';
    }
    if (!CW_URL) return '';
    var cwTag = ' data-okrev-cwvn="' + esc(r.productVariantName || '') + '" data-okrev-cwpn="' + esc(r.productName || '') + '"';
    return '<div class="okrev-block__media" hidden><div class="okrev-card__quotehero"' + cwTag + '></div></div>';
  }

  // Review-block media pane: main photo + swappable thumbnail strip (multi), main only
  // (single), or the no-photo colourway-swatch slot (none — see listNoPhotoMedia). Contained,
  // never bleeds.
  function mediaPane(r) {
    var photos = r.media || [];
    if (!photos.length) return listNoPhotoMedia(r);
    var first = photos[0];
    var main = '<div class="okrev-gallery__main" style="background-image:url(' + mediaUrl(first, 240) + ')">' +
      '<img src="' + esc(mediaUrl(first, 900)) + '" alt="' + esc(first.alt || cleanTitle(r) || '') + '" data-okrev-gmain loading="lazy" decoding="async" data-okrev-fade></div>';
    var thumbs = '';
    if (photos.length > 1) {
      thumbs = '<div class="okrev-gallery__thumbs">' + photos.map(function (m, i) {
        return '<button type="button" class="okrev-gallery__thumb' + (i === 0 ? ' is-active' : '') + '" data-okrev-gthumb' +
          ' data-src="' + esc(mediaUrl(m, 900)) + '" data-alt="' + esc(m.alt || '') + '" aria-label="View photo ' + (i + 1) + ' of ' + photos.length + '">' +
          '<img src="' + esc(mediaUrl(m, 160)) + '" alt="" loading="lazy"></button>';
      }).join('') + '</div>';
    }
    return '<div class="okrev-block__media"><div class="okrev-gallery">' + main + thumbs + '</div></div>';
  }

  function productHtml(r, source, pid) {
    // Hide the label only on the page's OWN product (a PDP showing its current
    // colorway) — there it's redundant. Pooled sibling colorways (grouped PDP) and
    // store/collection feeds keep it, so a review for a different colour is clearly
    // tagged with that colour ("Reviewed on …") and never read as if it's this one.
    if (!r.productName) return '';
    if (pid && r.productId === pid) return '';
    var thumb = '<span class="okrev-prod__thumb">' + (r.productImageUrl ? '<img src="' + esc(r.productImageUrl) + '" alt="" loading="lazy">' : '') + '</span>';
    var rating = r.productId ? '<span class="okrev-prod__rating" data-okrev-prod-rating="' + esc(r.productId) + '"></span>' : '';
    var info = '<span class="okrev-prod__info"><span class="okrev-prod__label">Reviewed on</span><span class="okrev-prod__name">' + esc(r.productName) + '</span>' + rating + '</span>';
    if (r.productUrl) {
      return '<a class="okrev-prod" href="' + esc(r.productUrl) + '" aria-label="' + esc(r.productName) + '">' +
        thumb + info + '<span class="okrev-prod__chevron" aria-hidden="true">›</span></a>';
    }
    return '<div class="okrev-prod okrev-prod--static">' + thumb + info + '</div>';
  }

  function replyHtml(r, show) {
    var body = (show && r.reply) ? htmlText(r.reply.body) : '';
    return body
      ? '<div class="okrev-card__reply"><b class="okrev-card__reply-label">' + esc(r.reply.title || 'hope&plum') + '</b>' +
        '<span class="okrev-card__reply-text">' + esc(body) + '</span></div>'
      : '';
  }

  // ── card variants ────────────────────────────────────────────────────────
  function cardMediaTop(r, opts, idx, tint) {
    var hasImg = r.media && r.media.length;
    var hasReply = opts.showReply && r.reply && r.reply.body;   // → tighter body clamp via CSS
    var title = cleanTitle(r);
    var hero = hasImg ? heroHtml(r.media, 700) : noPhotoHero(r, tint);
    // With a photo, the (uppercase) title sits above the body; no-photo cards use the
    // title AS the hero, so it isn't repeated in the content.
    var titleLine = (hasImg && title) ? '<p class="okrev-card__title">' + esc(title) + '</p>' : '';
    return '<article class="okrev-card okrev-card--mediatop' + (hasImg ? '' : ' okrev-card--noimg') + (hasReply ? ' okrev-card--has-reply' : '') + '">' +
      hero +
      '<div class="okrev-card__content">' +
        '<div class="okrev-card__top">' + starsHtml(r.rating) + '</div>' +
        titleLine + bodyBlock(r, idx) +
        replyHtml(r, opts.showReply) +
        authorBlock(r, idx) +
        productHtml(r, opts.source, opts.pid) +
      '</div>' +
    '</article>';
  }

  // Compact card: text only (no media, no quote/logo hero, no product pill). The whole
  // card links to the review's product. Brand reply included if present.
  function cardCompact(r, opts, idx) {
    var title = cleanTitle(r);
    var hasReply = opts.showReply && r.reply && r.reply.body;
    var inner = '<div class="okrev-card__content">' +
        '<div class="okrev-card__top">' + starsHtml(r.rating) + '</div>' +
        (title ? '<p class="okrev-card__title">' + esc(title) + '</p>' : '') +
        (r.body ? '<p class="okrev-card__text okrev-card__text--clamp">' + esc(r.body) + '</p>' : '') +
        replyHtml(r, opts.showReply) +
        authorBlock(r, idx) +
      '</div>';
    // Compact clamps the quote, so the whole card opens the lightbox to read it in full
    // (the lightbox also carries the product link).
    var cls = 'okrev-card okrev-card--compact okrev-card--clickable' + (hasReply ? ' okrev-card--has-reply' : '');
    return '<article class="' + cls + '" data-okrev-block="' + idx + '" role="button" tabindex="0" aria-label="Read ' + esc(reviewerName(r)) + '’s full review">' + inner + '</article>';
  }

  // Shared review block (desktop list rows + lightbox): media pane (left) + content pane.
  // Mobile collapses to the media-top card stack via CSS. Whole block opens the lightbox.
  function reviewBlock(r, opts, idx) {
    var isLb = opts.lightbox;
    var hasReply = !isLb && opts.showReply && r.reply && r.reply.body;   // replies never in the lightbox
    var title = cleanTitle(r);
    var body = isLb
      ? (r.body ? '<p class="okrev-card__text">' + esc(r.body) + '</p>' : '')   // full text, no clamp
      : bodyBlock(r, idx);
    return '<article class="okrev-block' + (isLb ? ' okrev-block--lb' : '') + (hasReply ? ' okrev-card--has-reply' : '') + '"' +
      (isLb ? '' : ' data-okrev-block="' + idx + '"') + '>' +
      mediaPane(r) +
      '<div class="okrev-block__content">' +
        (isLb ? '<button type="button" class="okrev-lb__close" data-okrev-lb-close aria-label="Close">✕</button>' : '') +
        '<div class="okrev-card__top">' + starsHtml(r.rating) + '</div>' +
        (title ? '<p class="okrev-card__title">' + esc(title) + '</p>' : '') +
        body +
        (hasReply ? replyHtml(r, true) : '') +
        authorBlock(r, idx) +
        productHtml(r, opts.source, opts.pid) +
      '</div>' +
    '</article>';
  }

  function wallTile(m, i, fallbackAlt) {
    var url = mediaUrl(m, 500); if (!url) return '';
    var vid = m.type === 'video' ? ' okrev-tile--video' : '';
    return '<button type="button" class="okrev-tile okrev-tile--clickable' + vid + '" data-okrev-tile="' + i + '">' +
      '<img src="' + esc(url) + '" alt="' + esc(m.alt || fallbackAlt || '') + '" loading="lazy"></button>';
  }

  function summaryHtml(aggregate, mediaReviews, showPhotos, stripOut, recommendLabel) {
    if (!aggregate) return '';
    var a = aggregate.reviewAggregate || aggregate;
    var count = a.reviewCount || a.ratingAndReviewCount || 0;
    if (!count) return '';                       // total === 0 → no summary (empty state handled elsewhere)
    var rc = a.ratingAndReviewCount || count;
    var avg = rc ? (a.ratingAndReviewValuesTotal || 0) / rc : 0;
    var byLevel = a.reviewCountByLevel || a.ratingAndReviewCountByLevel || {};

    var rows = [5, 4, 3, 2, 1].map(function (n, idx) {
      var c = byLevel['level' + n + 'Count'] || 0;
      var pct = count ? (c / count * 100) : 0;
      if (c > 0 && pct < 2) pct = 2;             // min visible width so rare buckets register
      // Target width as a custom prop so CSS can animate 0 → width on reveal (staggered per row).
      return '<div class="okrev-sum__row">' +
        '<span class="okrev-sum__row-label" aria-hidden="true">' + n + '</span>' +
        '<span class="okrev-sum__row-star" aria-hidden="true"></span>' +
        '<span class="okrev-sum__bar" role="meter" aria-valuemin="0" aria-valuemax="' + count + '" aria-valuenow="' + c +
          '" aria-label="' + n + ' stars: ' + c.toLocaleString() + ' reviews">' +
          '<span class="okrev-sum__bar-fill" style="--okrev-bar-w:' + pct + '%;--okrev-bar-d:' + (idx * 0.08) + 's"></span></span>' +
        '<span class="okrev-sum__rcount">' + c.toLocaleString() + '</span>' +
      '</div>';
    }).join('');

    // Recommend % — its own header column, mirroring the star-rating block. Floored like the
    // rating (Okendo shows 99%, not 100%, for 8145/8184).
    var rec = a.recommendationCount || 0;
    var recPct = (recommendLabel && rec && count) ? Math.floor(rec / count * 100) : 0;
    var recHtml = recPct ? '<div class="okrev-sum__rec"><span class="okrev-sum__rec-pct">' + recPct + '%</span><span class="okrev-sum__rec-label">' + esc(recommendLabel) + '</span></div>' : '';
    var score = '<div class="okrev-sum__score">' +
      '<span class="okrev-sum__avg" aria-hidden="true">' + avg.toFixed(2) + '</span>' + starsHtml(avg, 'okrev-stars--lg') +
      '<span class="okrev-sum__count">' + count.toLocaleString() + ' reviews</span></div>';
    // 3 parts: star rating · distribution bars · recommend %.
    var main = '<div class="okrev-sum__main">' + score + '<div class="okrev-sum__dist">' + rows + '</div>' + recHtml + '</div>';

    var strip = '';
    if (showPhotos && mediaReviews && mediaReviews.length) {
      var thumbs = '';
      mediaReviews.forEach(function (r) {
        (r.media || []).forEach(function (m) {
          if (stripOut.length >= 12) return;
          var url = mediaUrl(m, 200); if (!url) return;
          var i = stripOut.length; stripOut.push({ r: r, m: m });
          thumbs += '<li><button type="button" class="okrev-tile okrev-tile--clickable" data-okrev-strip="' + i + '">' +
            '<img src="' + esc(url) + '" alt="' + esc(m.alt || r.title || 'Customer photo') + '" loading="lazy"></button></li>';
        });
      });
      if (thumbs) {
        strip = '<div class="okrev-sum__strip"><p class="okrev-sum__strip-label">From recent reviews</p>' +
          '<ul class="okrev-sum__photos" aria-label="Recent review photos">' + thumbs + '</ul></div>';
      }
    }
    return '<div class="okrev-sum">' + main + strip + '</div>';
  }

  // ── lazy per-product ratings (non-blocking) ──────────────────────────────
  function fillProductRatings(el, store) {
    store = store || el.getAttribute('data-store-id');
    var spans = el.querySelectorAll('[data-okrev-prod-rating]');
    var byId = {};
    spans.forEach(function (s) { var id = s.getAttribute('data-okrev-prod-rating'); if (id) (byId[id] = byId[id] || []).push(s); });
    Object.keys(byId).forEach(function (pid) {
      var apply = function (agg) {
        if (!agg) return;
        var a = agg.reviewAggregate || agg;
        var c = a.reviewCount || 0;
        if (!c) return;
        var rc = a.ratingAndReviewCount || c;
        var avg = ((a.ratingAndReviewValuesTotal || 0) / rc).toFixed(2);   // two decimals: 4.97, 5.00
        byId[pid].forEach(function (s) { s.innerHTML = '<span class="okrev-prod__stars">' + avg + ' ★</span> · ' + c.toLocaleString() + ' reviews'; });
      };
      if (PROD_AGG[pid]) { apply(PROD_AGG[pid]); return; }
      getJSON(API_BASE + encodeURIComponent(store) + '/products/' + encodeURIComponent(pid) + '/review_aggregate')
        .then(function (d) { PROD_AGG[pid] = d; apply(d); }).catch(function () {});
    });
  }

  // ── lightbox: the shared review block at modal scale, inside a dialog ──────
  function getLightbox() {
    if (LB) return LB;
    LB = document.createElement('div');
    LB.className = 'okrev-lb';
    LB.hidden = true;
    LB.setAttribute('style', '--okrev-star:var(--hp-star); --okrev-star-empty:var(--hp-peach); --okrev-video-fg:#ffffff; --okrev-lb-panel:#ffffff; --okrev-nav-bg:#ffffff; --okrev-nav-fg:var(--hp-ink); --hp-adaptive-bg:#ffffff; --hp-sub-adaptive-bg:var(--hp-cream, #FDF7F3);');
    LB.innerHTML = '<div class="okrev-lb__backdrop" data-okrev-lb-close></div>' +
      '<div class="okrev-lb__panel" role="dialog" aria-modal="true"></div>';
    document.body.appendChild(LB);
    return LB;
  }
  function openLightbox(r) {
    var trigger = document.activeElement;
    var lb = getLightbox();
    var panel = lb.querySelector('.okrev-lb__panel');
    panel.setAttribute('aria-label', 'Review by ' + reviewerName(r));
    panel.innerHTML = reviewBlock(r, { lightbox: true, source: 'store' }, 0);
    panel.querySelectorAll('[data-okrev-gmain]').forEach(function (img) { if (img.complete && img.parentNode) img.parentNode.classList.add('is-loaded'); });
    if (STORE_ID) fillProductRatings(panel, STORE_ID);
    lb.__trigger = trigger;
    lb.hidden = false;
    // Next frame so the transition runs from the [hidden]→shown start state (fade + scale in).
    requestAnimationFrame(function () { requestAnimationFrame(function () { lb.classList.add('is-open'); }); });
    document.documentElement.style.overflow = 'hidden';
    var c = panel.querySelector('[data-okrev-lb-close]'); if (c) c.focus();
  }
  function closeLightbox() {
    if (!LB || LB.hidden) return;
    LB.classList.remove('is-open');
    document.documentElement.style.overflow = '';
    var trig = LB.__trigger;
    // Wait out the fade before display:none (skip the wait under reduced motion).
    var done = function () { if (LB) LB.hidden = true; if (trig && trig.focus) trig.focus(); };
    if (REDUCED) { done(); } else { setTimeout(done, 240); }
  }

  // ── reveal-on-scroll: star pop, summary bar fill, stat count-up ─────────────
  var REDUCED = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  var REVEAL_IO = null;
  function getRevealIO() {
    if (REVEAL_IO) return REVEAL_IO;
    if (!('IntersectionObserver' in window)) return null;
    REVEAL_IO = new IntersectionObserver(function (entries, obs) {
      entries.forEach(function (e) { if (e.isIntersecting) { obs.unobserve(e.target); revealHit(e.target); } });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.25 });
    return REVEAL_IO;
  }
  function revealHit(t) {
    if (t.classList.contains('okrev-stars')) t.classList.add('okrev-stars--pop');
    else if (t.classList.contains('okrev-sum')) t.classList.add('okrev-sum--revealed');
    else if (t.classList.contains('hp-stars')) t.style.setProperty('--hp-star-fill', t.__okStarTarget || '0%');
    else if (t.__okrevCountTo != null) runCountUp(t);
  }
  // Above-the-fold elements (PDP rating, homepage stats) are already in view at init, so a
  // scroll trigger never fires — animate them shortly after load instead, as a deliberate
  // entrance. Below the fold → fire on scroll-in.
  function revealOrObserve(el) {
    var io = getRevealIO();
    if (!io) { revealHit(el); return; }
    var r = el.getBoundingClientRect();
    var vh = window.innerHeight || document.documentElement.clientHeight;
    if (r.bottom > 0 && r.top < vh) setTimeout(function () { revealHit(el); }, 240);
    else io.observe(el);
  }
  // Arm (hide/zero) the animatable bits, then let the IO fire them. With no IO or reduced
  // motion we never arm, so everything stays visible at its final state.
  function observeReveals(scope) {
    var io = getRevealIO();
    if (!io || REDUCED) return;
    // Only new (unseen) elements — load-more appends, so don't re-arm already-revealed cards.
    var fresh = [];
    scope.querySelectorAll('.okrev-stars').forEach(function (s) { if (!s.__okrevReveal) { s.__okrevReveal = 1; s.classList.add('okrev-stars--armed'); fresh.push(s); } });
    var sum = scope.querySelector('.okrev-sum');
    var doSum = sum && !sum.__okrevReveal;
    if (doSum) { sum.__okrevReveal = 1; sum.classList.add('okrev-sum--armed'); }
    void scope.offsetWidth;   // force a paint of the armed (0) state so the reveal always transitions
    fresh.forEach(function (s) { revealOrObserve(s); });
    if (doSum) revealOrObserve(sum);
  }
  // The "cute" fractional .hp-stars (sbar + PDP rating) don't pop per-star, but sweep their
  // fill 0 → rating on scroll-in. Arm to 0 here; revealHit restores the real fill.
  function initStarSweeps(scope) {
    if (REDUCED || !getRevealIO()) return;
    scope.querySelectorAll('.hp-stars').forEach(function (el) {
      if (el.__okStarArmed || el.hasAttribute('data-okrev-stat-stars')) return;   // sbar stat stars: fillStoreStats arms them
      var target = (el.style.getPropertyValue('--hp-star-fill') || '').trim();
      if (!target || target === '0%') return;
      el.__okStarArmed = true; el.__okStarTarget = target;
      el.classList.add('hp-stars--reveal');
      el.style.setProperty('--hp-star-fill', '0%');
      revealOrObserve(el);
    });
  }
  function setCountVal(el, v, decimals) {
    el.textContent = decimals ? (Math.round(v * 100) / 100).toFixed(2) : Math.round(v).toLocaleString();
  }
  // Tick a number up to its target over ~1.4s (easeOutCubic). In view immediately or via IO.
  function prepCountUp(el, target, decimals) {
    if (REDUCED || !getRevealIO()) { setCountVal(el, target, decimals); return; }
    el.__okrevCountTo = target; el.__okrevDecimals = decimals;
    setCountVal(el, 0, decimals);
    revealOrObserve(el);
  }
  function runCountUp(el) {
    var target = el.__okrevCountTo, decimals = el.__okrevDecimals, dur = 1400, start = null;
    function frame(ts) {
      if (start == null) start = ts;
      var p = Math.min(1, (ts - start) / dur);
      setCountVal(el, target * (1 - Math.pow(1 - p, 3)), decimals);
      if (p < 1) requestAnimationFrame(frame); else setCountVal(el, target, decimals);
    }
    requestAnimationFrame(frame);
  }

  // ── render ────────────────────────────────────────────────────────────────
  // Cards for a batch of reviews (grid/list/compact), indices continuing from startIdx so
  // Read-more/lightbox lookups stay aligned with el.__okrevReviews across appended pages.
  function buildCards(reviews, opts, layout, startIdx) {
    startIdx = startIdx || 0;
    if (layout === 'list') {
      return reviews.map(function (r, i) { return reviewBlock(r, opts, startIdx + i); }).join('');
    }
    if (layout === 'compact') {
      return reviews.map(function (r, i) { return cardCompact(r, opts, startIdx + i); }).join('');
    }
    var tint = 0;   // rotate brand tints across no-photo heroes only
    return reviews.map(function (r, i) { return cardMediaTop(r, opts, startIdx + i, (r.media && r.media.length) ? 0 : tint++); }).join('');
  }
  // Post-render wiring; idempotent so it can run again over appended cards (flags skip old ones).
  function postRender(el) {
    fillProductRatings(el);
    el.querySelectorAll('[data-okrev-clamp]').forEach(function (p) {
      if (p.__okrevClampDone) return; p.__okrevClampDone = 1;
      if (p.scrollHeight - p.clientHeight > 2) {
        var btn = p.nextElementSibling;
        if (btn && btn.classList.contains('okrev-card__more')) btn.hidden = false;
      }
    });
    observeReveals(el);   // star pop-in + summary bar fill on scroll-into-view
  }
  function render(el, data) {
    var layout = el.getAttribute('data-layout') || 'grid';
    SWATCH_MAP = readJsonScript(el, 'data-okrev-swatches');         // id → swatch (active colourways), inlined per PDP
    CW_URL = el.getAttribute('data-okrev-cw-url') || null;          // lazy-fetched retired-colourway fallback (off critical path)
    // Pinned reviews are hand-picked + ordered — render verbatim, no filtering/slicing.
    // Grouped PDP: keep the full floated pool (current-product-first) so Load more reveals the
    // current product's reviews before paging into older siblings; show only `limit` to start.
    var grouped = isGrouped(el);
    var reviews, pool = null;
    if (el.getAttribute('data-source') === 'pinned') {
      reviews = data.reviews || [];
    } else if (grouped) {
      pool = filterReviews(data.reviews || [], el, true);   // full set, current-product floated to front
      reviews = pool.slice(0, parseInt(el.getAttribute('data-limit'), 10) || 12);
    } else {
      reviews = filterReviews(data.reviews || [], el);
    }
    el.__okrevPool = pool;         // grouped: client-paginate this before the cursor; else null
    el.__okrevReviews = reviews;   // for Read-more → lightbox lookups (grows as pages append)
    el.__okrevNext = data.nextUrl ? (API_ROOT + data.nextUrl) : null;   // load-more cursor (continues after the pool)
    var wantSummary = el.getAttribute('data-show-summary') === 'true';
    var wantPhotos = wantSummary && el.getAttribute('data-summary-photos') === 'true';
    var strip = [];
    var summary = wantSummary ? summaryHtml(data.aggregate, data.mediaReviews, wantPhotos, strip, el.getAttribute('data-summary-recommend') || '') : '';
    // Keep the Product JSON-LD rating in sync with the precise value we display (see patchProductJsonLd).
    if (el.getAttribute('data-product-id')) patchProductJsonLd(data.aggregate);
    el.__okrevStrip = strip;
    var opts = { showReply: el.getAttribute('data-show-reply') === 'true', source: el.getAttribute('data-source'), pid: el.getAttribute('data-product-id'), layout: layout, requireMedia: el.getAttribute('data-require-media') === 'true' };
    el.__okrevOpts = opts; el.__okrevLayout = layout;
    var inner = '';
    if (layout === 'photo_wall') {
      var max = Math.max(parseInt(el.getAttribute('data-limit'), 10) || 12, 12);
      var items = [];
      reviews.forEach(function (r) { (r.media || []).forEach(function (m) { items.push({ r: r, m: m }); }); });
      items = items.slice(0, max);
      el.__okrevWall = items;
      inner = items.map(function (it, i) { return wallTile(it.m, i, it.r.title); }).join('');
    } else {
      inner = buildCards(reviews, opts, layout, 0);
    }
    if (!inner && !summary) { el.innerHTML = '<p class="okrev__empty">No reviews yet.</p>'; return; }
    if (!inner) inner = '<p class="okrev__empty">' + (el.getAttribute('data-require-media') === 'true' ? 'No reviews with photos yet.' : 'No reviews yet.') + '</p>';
    // Toggle renders between the summary and the list (not above the stats) so it sits directly atop the cards it filters.
    var filter = (el.getAttribute('data-show-filter') === 'true' && layout !== 'photo_wall') ? filterHtml(el) : '';
    el.innerHTML = summary + filter + '<div class="okrev__items okrev__items--' + layout + '">' + inner + '</div>';
    postRender(el);
    setupLoadMore(el);
    // Any photoless card that missed the local id-map was tagged for name-match. If some exist
    // and the global map isn't loaded yet, lazy-fetch it and upgrade those cards in place.
    if (CW_URL && layout !== 'photo_wall' && el.querySelector('[data-okrev-cwpn]')) ensureCwMap(el);
  }

  // ── load more (cursor pagination) ──────────────────────────────────────────
  function loadMoreBtn(el) { var r = el.closest('.okrev'); return r ? r.querySelector('[data-okrev-loadmore]') : null; }
  function setupLoadMore(el) {
    var btn = loadMoreBtn(el); if (!btn) return;
    var source = el.getAttribute('data-source');
    // Pinned (fixed IDs), keyword (already page-walked) and photo-wall don't cursor-paginate here.
    var paginatable = source !== 'pinned' && !el.getAttribute('data-keyword') && el.getAttribute('data-layout') !== 'photo_wall';
    var poolHasMore = el.__okrevPool && (el.__okrevReviews || []).length < el.__okrevPool.length;
    btn.hidden = !(paginatable && (poolHasMore || el.__okrevNext));
  }
  function loadMore(el, btn) {
    // Phase 1 (grouped): reveal more of the already-floated pool — current-product reviews lead it.
    // Synchronous (no fetch); the cursor isn't touched until the pool is exhausted.
    var pool = el.__okrevPool;
    if (pool && (el.__okrevReviews || []).length < pool.length) {
      var plim = parseInt(el.getAttribute('data-limit'), 10) || 12;
      var pstart = (el.__okrevReviews || []).length;
      var pbatch = pool.slice(pstart, pstart + plim);
      CW_URL = el.getAttribute('data-okrev-cw-url') || null;
      var pItems = el.querySelector('.okrev__items');
      if (pItems && pbatch.length) pItems.insertAdjacentHTML('beforeend', buildCards(pbatch, el.__okrevOpts, el.__okrevLayout, pstart));
      el.__okrevReviews = (el.__okrevReviews || []).concat(pbatch);
      postRender(el);
      if (CW_URL && el.querySelector('[data-okrev-cwpn]')) ensureCwMap(el);
      setupLoadMore(el);
      return;
    }
    // Phase 2: pool exhausted (or non-grouped feed) — page the API cursor.
    if (!el.__okrevNext || btn.__okrevLoading) return;
    btn.__okrevLoading = true; btn.disabled = true; btn.classList.add('is-loading');
    CW_URL = el.getAttribute('data-okrev-cw-url') || null;   // so appended photoless cards tag for the same fallback
    getJSON(el.__okrevNext).then(function (d) {
      var batch = filterReviews(d.reviews || [], el, true);   // no slice — append all that pass
      var startIdx = (el.__okrevReviews || []).length;
      el.__okrevReviews = (el.__okrevReviews || []).concat(batch);
      var itemsEl = el.querySelector('.okrev__items');
      if (itemsEl && batch.length) itemsEl.insertAdjacentHTML('beforeend', buildCards(batch, el.__okrevOpts, el.__okrevLayout, startIdx));
      el.__okrevNext = d.nextUrl ? (API_ROOT + d.nextUrl) : null;
      postRender(el);
      // Appended cards can include retired-colourway photoless reviews (deep in the feed) → upgrade them too.
      if (CW_URL && el.querySelector('[data-okrev-cwpn]')) ensureCwMap(el);
    }).catch(function () {}).then(function () {
      btn.__okrevLoading = false; btn.disabled = false; btn.classList.remove('is-loading');
      setupLoadMore(el);   // hide when no more pages
    });
  }

  // Fill curated stats-strip placeholders (homepage) from the store-wide aggregate.
  function fillStoreStats(el) {
    var root = el.closest('.okrev'); if (!root) return;
    var ratingEl = root.querySelector('[data-okrev-stat-rating]');
    var starsEl = root.querySelector('[data-okrev-stat-stars]');
    var countEl = root.querySelector('[data-okrev-stat-count]');
    if (!ratingEl && !starsEl && !countEl) return;
    var aUrl = aggregateUrl(el); if (!aUrl) return;
    // Start at 0 immediately (before the fetch) so the server placeholder never shows then jumps —
    // the value only ticks up from 0. The real numbers still live in the server JSON-LD.
    if (!REDUCED && getRevealIO()) {
      if (ratingEl) ratingEl.textContent = '0.00';
      if (countEl) countEl.textContent = '0';
      if (starsEl) { starsEl.classList.add('hp-stars--reveal'); starsEl.style.setProperty('--hp-star-fill', '0%'); }
    }
    getJSON(aUrl).then(function (data) {
      var a = (data && data.reviewAggregate) || {};
      var count = a.reviewCount || a.ratingAndReviewCount || 0;
      var total = a.reviewRatingValuesTotal || a.ratingAndReviewValuesTotal || 0;
      var avg = count ? total / count : 0;
      if (avg) {
        // Round to 2dp (consistent with product-rating + JSON-LD round:2) so a genuine 5.0
        // shows as 5.00. The pre-JS placeholder is the manual stat value (block.settings.metric);
        // JS replaces it here with the live Okendo aggregate.
        if (ratingEl) prepCountUp(ratingEl, Math.round(avg * 100) / 100, true);   // ticks 0.00 → 4.97
        if (starsEl) {
          var pct = Math.max(0, Math.min(100, avg / 5 * 100)) + '%';
          if (REDUCED || !getRevealIO()) starsEl.style.setProperty('--hp-star-fill', pct);
          else { starsEl.__okStarTarget = pct; revealOrObserve(starsEl); }   // sweep on reveal (load if in view)
        }
      }
      if (countEl && count) prepCountUp(countEl, Number(count), false);          // ticks up to e.g. 8,183
    }).catch(function () {});
  }

  // Keyword feeds: walk pages via nextUrl, collecting matches until we have `limit`
  // (or hit the page cap, so a rare theme can't fan out into endless requests).
  function loadKeyword(el) {
    var startUrl = reviewsUrl(el);
    if (!startUrl) { render(el, { reviews: [] }); return; }
    var terms = (el.getAttribute('data-keyword') || '').toLowerCase().split(',').map(function (s) { return s.trim(); }).filter(Boolean);
    var min = parseInt(el.getAttribute('data-min-rating'), 10) || 0;
    var requireMedia = el.getAttribute('data-require-media') === 'true';
    var limit = parseInt(el.getAttribute('data-limit'), 10) || 12;
    var MAX_PAGES = 10;
    var POOL = Math.max(limit, limit * 4);   // gather a pool, then rank for preview-visibility
    var matched = [];
    function ok(r) {
      if (min && (r.rating || 0) < min) return false;
      if (requireMedia && !(r.media && r.media.length)) return false;
      var hay = ((r.body || '') + ' ' + (r.title || '')).toLowerCase();
      for (var i = 0; i < terms.length; i++) { if (hay.indexOf(terms[i]) >= 0) return true; }
      return false;
    }
    // Rank so the term shows in the *visible* part: title match > early-body match > later.
    function score(r) {
      var t = (r.title || '').toLowerCase(), b = (r.body || '').slice(0, 160).toLowerCase(), s = 0;
      for (var i = 0; i < terms.length; i++) { if (t.indexOf(terms[i]) >= 0) s += 3; else if (b.indexOf(terms[i]) >= 0) s += 1; }
      return s;
    }
    function done() {
      matched.sort(function (a, b) { return score(b) - score(a); });   // stable → ties keep recency
      render(el, { reviews: matched.slice(0, limit) });
    }
    function page(url, n) {
      return getJSON(url).then(function (d) {
        (d.reviews || []).forEach(function (r) { if (matched.length < POOL && ok(r)) matched.push(r); });
        if (matched.length >= POOL || !d.nextUrl || n >= MAX_PAGES) { done(); return; }
        return page(API_ROOT + d.nextUrl, n + 1);
      });
    }
    page(startUrl, 1).catch(done);
  }

  function load(el) {
    if (el.getAttribute('data-okrev-loaded')) return;
    el.setAttribute('data-okrev-loaded', '1');
    STORE_ID = el.getAttribute('data-store-id');
    fillStoreStats(el);

    // Keyword filter walks multiple pages — route here before the single-page path.
    if (el.getAttribute('data-keyword')) { loadKeyword(el); return; }

    // Pinned mode: fetch each hand-picked review by reviewId, preserve order, render verbatim.
    // Pin blocks (with an optional location caption) take priority over the plain ID list.
    if (el.getAttribute('data-source') === 'pinned') {
      var store = el.getAttribute('data-store-id');
      var pinEls = el.querySelectorAll('[data-okrev-pin]');
      var pins = [];
      if (pinEls.length) {
        for (var pi = 0; pi < pinEls.length; pi++) {
          var pid = pinEls[pi].getAttribute('data-id');
          if (pid) pins.push({ id: pid, loc: pinEls[pi].getAttribute('data-loc') || '' });
        }
      } else {
        pins = (el.getAttribute('data-pinned-ids') || '').split(/[\s,]+/).filter(Boolean).map(function (id) { return { id: id, loc: '' }; });
      }
      if (!store || !pins.length) { el.innerHTML = '<p class="okrev__empty">No reviews pinned yet.</p>'; return; }
      var base = API_BASE + encodeURIComponent(store) + '/reviews/';
      Promise.all(pins.map(function (pin) {
        return getJSON(base + encodeURIComponent(pin.id)).then(function (d) { var r = (d && d.review) || null; if (r && pin.loc) r._pinLocation = pin.loc; return r; }).catch(function () { return null; });
      })).then(function (arr) {
        render(el, { reviews: arr.filter(Boolean) });
      }).catch(function () { el.innerHTML = '<p class="okrev__empty">Reviews couldn’t load right now.</p>'; });
      return;
    }

    var rUrl = reviewsUrl(el);
    if (!rUrl) { el.innerHTML = '<p class="okrev__empty">Reviews unavailable.</p>'; return; }
    var wantSummary = el.getAttribute('data-show-summary') === 'true';
    var wantPhotos = wantSummary && el.getAttribute('data-summary-photos') === 'true';
    var jobs = [getJSON(rUrl).catch(function () { return { reviews: [] }; })];
    if (wantSummary) {
      jobs.push(getJSON(aggregateUrl(el)).catch(function () { return null; }));
      jobs.push(wantPhotos ? getJSON(mediaStripUrl(el)).catch(function () { return { reviews: [] }; }) : Promise.resolve({ reviews: [] }));
    }
    Promise.all(jobs).then(function (res) {
      render(el, { reviews: (res[0] || {}).reviews || [], nextUrl: (res[0] || {}).nextUrl, aggregate: wantSummary ? res[1] : null, mediaReviews: wantSummary ? ((res[2] || {}).reviews || []) : [] });
    }).catch(function () { el.innerHTML = '<p class="okrev__empty">Reviews couldn’t load right now.</p>'; });
  }

  // Lazy-load the global retired-colourway swatch map, once, the first time a feed renders a
  // photoless card the local id-map couldn't resolve. Kept off the PDP critical path: the map
  // lives at its own (full-page-cached) URL and is only fetched on demand. On arrival, every
  // queued feed upgrades its tagged pastel cards to swatches.
  function ensureCwMap(el) {
    if (CW_SWATCH_MAP) { upgradePastelCards(el); return; }   // already loaded → upgrade immediately
    if (CW_PENDING.indexOf(el) === -1) CW_PENDING.push(el);
    if (CW_PROMISE) return;                                   // fetch already in flight
    if (!CW_URL) return;                                      // not configured → pastel stays
    CW_PROMISE = fetch(CW_URL, { headers: { Accept: 'application/json' } })
      .then(function (res) { return res.ok ? res.json() : {}; })
      .catch(function () { return {}; })                       // 404 / parse error → empty map, pastel stays
      .then(function (map) {
        CW_SWATCH_MAP = map || {};
        CW_PENDING.forEach(upgradePastelCards);
        CW_PENDING = [];
      });
  }
  // Swap any tagged pastel/pending hero in `el` for its colourway swatch, now that the global
  // map is in. Mirrors noPhotoHero's swatch branch as a DOM mutation (no re-render → no summary
  // re-animation). List rows tag a hidden `.okrev-block__media` slot (listNoPhotoMedia) instead
  // of a pastel hero — reveal it here too once a match lands; leave it hidden if there's still no match.
  function upgradePastelCards(el) {
    var heroes = el.querySelectorAll('[data-okrev-cwpn]');
    for (var i = 0; i < heroes.length; i++) {
      var h = heroes[i];
      var sw = cwSwatchForName(h.getAttribute('data-okrev-cwvn'), h.getAttribute('data-okrev-cwpn'));
      h.removeAttribute('data-okrev-cwvn'); h.removeAttribute('data-okrev-cwpn');  // processed either way
      if (!sw) continue;                                       // still no match → leave pastel/hidden
      h.classList.add('okrev-card__quotehero--swatch');
      h.classList.remove('okrev-card__quotehero--logo');
      h.style.background = '';
      h.style.backgroundImage = 'url(' + sw + ')';
      var logo = h.querySelector('.okrev-card__herologo');
      if (logo) logo.parentNode.removeChild(logo);             // swatch IS the visual → drop the logo
      if (!h.querySelector('.okrev-card__scrim')) {            // ensure the cream legibility scrim
        var scrim = document.createElement('span');
        scrim.className = 'okrev-card__scrim';
        scrim.setAttribute('aria-hidden', 'true');
        h.insertBefore(scrim, h.firstChild);
      }
      var wrap = h.closest('.okrev-block__media');
      if (wrap && wrap.hidden) wrap.hidden = false;
    }
  }

  // "With photos" toggle: flip the media requirement and re-query (require_media=true also
  // switches the fetch order to has_media desc in reviewsUrl). Resets paging so Load more
  // continues with the filtered set; reuses load() with the guard cleared so it re-runs.
  function applyPhotoFilter(el, on) {
    el.setAttribute('data-require-media', on ? 'true' : 'false');
    el.removeAttribute('data-okrev-loaded');
    el.__okrevReviews = null; el.__okrevNext = null; el.__okrevSeen = {}; el.__okrevPool = null;
    load(el);
  }

  // Segmented "All reviews / With photos" pill, rendered inside the feed between summary
  // and list. Active state derives from the live data-require-media flag so a re-render
  // (after a toggle re-query) reflects the current filter.
  function filterHtml(el) {
    var photos = el.getAttribute('data-require-media') === 'true';
    return '<div class="okrev__filter" role="group" aria-label="Filter reviews">' +
      '<button type="button" class="okrev__filter-btn' + (photos ? '' : ' is-active') + '" data-okrev-filter="all" aria-pressed="' + (photos ? 'false' : 'true') + '">All reviews</button>' +
      '<button type="button" class="okrev__filter-btn' + (photos ? ' is-active' : '') + '" data-okrev-filter="photos" aria-pressed="' + (photos ? 'true' : 'false') + '">With photos</button>' +
      '</div>';
  }

  // ── interactivity (delegated, attached once) ─────────────────────────────
  document.addEventListener('click', function (e) {
    var fbtn = e.target.closest('[data-okrev-filter]');
    if (fbtn) {
      var froot = fbtn.closest('.okrev');
      var ffeed = froot && froot.querySelector('[data-okendo-feed]');
      if (ffeed && !fbtn.classList.contains('is-active')) {
        var photos = fbtn.getAttribute('data-okrev-filter') === 'photos';
        froot.querySelectorAll('[data-okrev-filter]').forEach(function (b) {
          var active = (b.getAttribute('data-okrev-filter') === 'photos') === photos;
          b.classList.toggle('is-active', active);
          b.setAttribute('aria-pressed', active ? 'true' : 'false');
        });
        applyPhotoFilter(ffeed, photos);
      }
      return;
    }
    var lmore = e.target.closest('[data-okrev-loadmore]');
    if (lmore) {
      var lfeed = lmore.closest('.okrev') && lmore.closest('.okrev').querySelector('[data-okendo-feed]');
      if (lfeed) loadMore(lfeed, lmore);
      return;
    }
    var arrow = e.target.closest('[data-okrev-prev], [data-okrev-next]');
    if (arrow) {
      var track = arrow.closest('.okrev-hero').querySelector('.okrev-hero__track');
      if (track) track.scrollBy({ left: (arrow.hasAttribute('data-okrev-next') ? 1 : -1) * track.clientWidth, behavior: 'smooth' });
      return;
    }
    var gthumb = e.target.closest('[data-okrev-gthumb]');
    if (gthumb) {
      var gal = gthumb.closest('.okrev-gallery');
      var gmain = gal && gal.querySelector('[data-okrev-gmain]');
      if (gmain) { gmain.src = gthumb.getAttribute('data-src'); gmain.alt = gthumb.getAttribute('data-alt') || ''; }
      gal.querySelectorAll('.okrev-gallery__thumb').forEach(function (t) { t.classList.remove('is-active'); });
      gthumb.classList.add('is-active');
      return;
    }
    var tile = e.target.closest('[data-okrev-tile]');
    if (tile) {
      var feed = tile.closest('[data-okendo-feed]');
      var it = feed && feed.__okrevWall && feed.__okrevWall[parseInt(tile.getAttribute('data-okrev-tile'), 10)];
      if (it) openLightbox(it.r);
      return;
    }
    var strip = e.target.closest('[data-okrev-strip]');
    if (strip) {
      var feedS = strip.closest('[data-okendo-feed]');
      var its = feedS && feedS.__okrevStrip && feedS.__okrevStrip[parseInt(strip.getAttribute('data-okrev-strip'), 10)];
      if (its) openLightbox(its.r);
      return;
    }
    var more = e.target.closest('[data-okrev-more]');
    if (more) {
      var feedM = more.closest('[data-okendo-feed]');
      var rm = feedM && feedM.__okrevReviews && feedM.__okrevReviews[parseInt(more.getAttribute('data-okrev-more'), 10)];
      if (rm) openLightbox(rm);
      return;
    }
    // Whole list block opens the lightbox — except the product link (let it navigate).
    var block = e.target.closest('[data-okrev-block]');
    if (block && !e.target.closest('.okrev-prod')) {
      var feedB = block.closest('[data-okendo-feed]');
      var rb = feedB && feedB.__okrevReviews && feedB.__okrevReviews[parseInt(block.getAttribute('data-okrev-block'), 10)];
      if (rb) { openLightbox(rb); return; }
    }
    if (e.target.closest('[data-okrev-lb-close]')) closeLightbox();
  });
  document.addEventListener('keydown', function (e) {
    // Open the lightbox from a keyboard-focused clickable card (compact).
    if ((e.key === 'Enter' || e.key === ' ') && e.target && e.target.matches && e.target.matches('[data-okrev-block][role="button"]')) {
      e.preventDefault(); e.target.click(); return;
    }
    if (!LB || LB.hidden) return;
    if (e.key === 'Escape') { closeLightbox(); return; }
    if (e.key === 'Tab') {   // focus trap
      var f = LB.querySelectorAll('a[href], button:not([disabled])');
      if (!f.length) return;
      var first = f[0], last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      return;
    }
    if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {   // step thumbnails
      var gal = LB.querySelector('.okrev-gallery');
      if (!gal) return;
      var thumbs = Array.prototype.slice.call(gal.querySelectorAll('.okrev-gallery__thumb'));
      if (thumbs.length < 2) return;
      var i = thumbs.indexOf(gal.querySelector('.okrev-gallery__thumb.is-active'));
      thumbs[(i + (e.key === 'ArrowRight' ? 1 : -1) + thumbs.length) % thumbs.length].click();
      e.preventDefault();
    }
  });
  // Progressive hero images: fade in once the high-res loads (capture — load doesn't bubble).
  document.addEventListener('load', function (e) {
    var img = e.target;
    if (img && img.tagName === 'IMG' && img.hasAttribute('data-okrev-fade') && img.parentNode) img.parentNode.classList.add('is-loaded');
  }, true);

  // ── boot ──────────────────────────────────────────────────────────────────
  function observe(el) {
    if (!('IntersectionObserver' in window)) { load(el); return; }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) { if (e.isIntersecting) { io.unobserve(e.target); load(e.target); } });
    }, { rootMargin: '300px 0px' });
    io.observe(el);
  }
  function init(root) {
    var scope = root || document;
    scope.querySelectorAll('[data-okendo-feed]').forEach(observe);
    initStarSweeps(scope);   // animate "cute" .hp-stars (PDP rating etc.) on scroll-in
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { init(); });
  else init();
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
})();
